import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type SiteAccessListProps = {
  siteNames: string[]
  collapsedCount?: number
  testId?: string
}

const StyledLabel = styled.div`
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.color.neutral.color4};
`

const StyledList = styled.ul`
  list-style: disc inside;
  margin: 4px 0 0 0;
  padding: 0;
  color: ${(props) => props.theme.color.neutral.color1};
  font-size: 16px;
  line-height: 24px;
`

const StyledListItem = styled.li`
  margin-left: 8px;
`

const StyledEmpty = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.color.neutral.color1};
`

const StyledToggle = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 4px 0;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${(props) => props.theme.color.status.primary};
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
`

const DEFAULT_COLLAPSED_COUNT = 2

const SiteAccessList: React.FC<SiteAccessListProps> = ({
  siteNames,
  collapsedCount = DEFAULT_COLLAPSED_COUNT,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const [expanded, setExpanded] = useState(false)

  const total = siteNames.length
  const showsAll = expanded || total <= collapsedCount
  const visible = showsAll ? siteNames : siteNames.slice(0, collapsedCount)
  const hiddenCount = total - collapsedCount

  return (
    <div data-testid={testId}>
      <StyledLabel>{t(reportsConstants.TEAM_LIST_SITE_ACCESS)}</StyledLabel>
      {total === 0 ? (
        <StyledEmpty
          data-testid={testId ? `${testId}-empty` : undefined}
          aria-label={t(reportsConstants.TEAM_LIST_NO_SITE_ACCESS)}
        >
          —
        </StyledEmpty>
      ) : (
        <StyledList data-testid={testId ? `${testId}-list` : undefined}>
          {visible.map((name) => (
            <StyledListItem key={name}>{name}</StyledListItem>
          ))}
        </StyledList>
      )}
      {total > collapsedCount ? (
        <StyledToggle
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {expanded ? (
            <>
              {t(reportsConstants.TEAM_LIST_SITE_SHOW_LESS)} <UpOutlined />
            </>
          ) : (
            <>
              {t(reportsConstants.TEAM_LIST_SITE_SHOW_MORE, {
                count: hiddenCount
              })}{' '}
              <DownOutlined />
            </>
          )}
        </StyledToggle>
      ) : null}
    </div>
  )
}

export default SiteAccessList
